import React, { useState } from "react";
import { Link } from "react-router-dom";
import { navigateToHomeSection } from '../utils/navigation';
import "./Aivionscore.css";

const capabilities = [
  {
    id: "01",
    title: "Automated Document Extraction",
    short: "Parse statutory accounts, management accounts and supporting schedules from PDF and structured.",
    detail: "Parse statutory accounts, management accounts and supporting schedules from PDF and structured files.",
  },
  {
    id: "02",
    title: "Explainable Risk Scoring",
    short: "ML models with feature-level explanations aligned to your risk appetite and policy.",
    detail: "Every score comes with a plain-language breakdown so analysts and credit committees can interrogate the output. Models are calibrated to your risk appetite and policy framework.",
  },
  {
    id: "03",
    title: "Covenant & Ratio Analysis",
    short: "Automated computation of financial covenants, leverage and coverage metrics.",
    detail: "Covenant breaches and near-misses are flagged in real time. Leverage, coverage and bespoke ratio metrics are computed automatically from ingested financials.",
  },
  {
    id: "04",
    title: "Portfolio Dashboards",
    short: "Real-time views of concentration, sector exposure and emerging risk signals.",
    detail: "Slice the portfolio by industry, geography, rating band or any custom dimension. Emerging risk signals surface automatically so credit teams act before issues escalate.",
  },
  {
    id: "05",
    title: "Workflow Integration",
    short: "Embed into origination systems with role-based views for analysts and credit committee.",
    detail: "Connect via API or pre-built connectors to your CRM, LOS, or document store. Role-based views ensure analysts, credit committee and risk teams see exactly what they need.",
  },
  {
    id: "06",
    title: "Model Risk & Governance",
    short: "Validation-ready documentation, monitoring and challenger model support.",
    detail: "Satisfy SR 11-7 and equivalent frameworks with automatically generated model cards and ongoing drift monitoring. Challenger model support keeps your scoring approach competitive.",
  },
];

const deploySteps = [
  { id: "01", title: "Calibrate", desc: "Train and validate scoring models against your historical portfolio and policy framework." },
  { id: "02", title: "Integrate", desc: "Connect to document stores, CRM and credit workflow systems." },
  { id: "03", title: "Deploy", desc: "Roll out to analysts with training, governance and independent validation." },
  { id: "04", title: "Monitor", desc: "Track performance, drift and business impact with ongoing model oversight." },
];

const outcomes = [
  "Up to 65% faster credit decisions on standard deals",
  "Consistent, explainable risk assessments across the portfolio",
  "Real-time visibility for credit committee and risk teams",
  "Reduced manual re-keying and analyst burnout",
  "Regulator-ready model documentation and monitoring",
];

const products = [
  { name: "aivionGrade", tag: "AI-Powered Exam Assessment", link: "https://aiviongrade.aivion.ai/" },
  { name: "aivionSales", tag: "AI-Based Sales & CRM", link: "/aivionsales" },
  { name: "DigitalDeputee", tag: "Digital Employees Platform", link: "/digitaldeputee" },
];

const navLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];
const footerLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];

export default function AivionScore() {
  const [active, setActive] = useState(0);

  return (
    <div className="av-root">

      {/* ── NAV ── */}
      <nav className="av-nav">
        <div className="av-nav-inner">
          <div className="av-logo-box">
            <span className="av-logo-aiv">AIV</span><span className="av-logo-ion">ION</span>
          </div>
          <ul className="av-nav-links">
            {navLinks.map((l) => <li key={l.label}><a href={l.href} onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a></li>)}
          </ul>
          <a href="/#contact" className="av-contact-btn" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>CONTACT US</a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="av-hero-wrap">
        <div className="av-hero">
          <div className="av-hero-left">
            <div className="av-breadcrumb">
              <span className="av-bc-arrow">&#8592;</span>
              <span className="av-bc-parent">AIVION PRODUCTS</span>
              <span className="av-bc-badge">&#9679; PRODUCT · LIVE</span>
            </div>
            <h1 className="av-hero-h1">aivionScore</h1>
            <p className="av-hero-sub">AI-Based Credit Review</p>
            <div className="av-hero-ctas">
              <a href="/#contact" className="av-btn-solid-outline" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>REQUEST A DEMO &#8594;</a>
              <button className="av-btn-ghost" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>TAKE THE TOUR</button>
            </div>
            <div className="av-hero-tags">
              <span>&#10011; ENTERPRISE-GRADE</span>
              <span>&#9711; WORKFLOW-NATIVE</span>
              <span>&#10022; DEPLOYS IN WEEKS</span>
            </div>
          </div>
          <div className="av-hero-right">
            <div className="av-browser">
              <div className="av-browser-topbar">
                <span className="av-dot" /><span className="av-dot" /><span className="av-dot" />
                <div className="av-urlbar">app.aivion.ai/aivion-score</div>
              </div>
              <div className="av-browser-screen">
                <div className="av-browser-inner-rect" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BUILT END TO END ── */}
      <section className="av-built">
        <div className="av-built-inner">
          <div className="av-built-left">
            <p className="av-eyebrow">THE PRODUCT</p>
            <h2 className="av-built-title">Built to do the job,<br />end-to-end.</h2>
          </div>
          <div className="av-built-right">
            <p>
              aivionScore accelerates credit decisions without sacrificing rigour. Designed for
              lenders, specialist finance providers and credit teams, it automates extraction from
              financial statements, applies explainable risk scoring calibrated to your portfolio,
              and surfaces portfolio concentration in real time. Analysts spend less time on data
              entry and more time on judgement — with every score auditable for model risk and
              regulatory review.
            </p>
          </div>
        </div>
      </section>

      {/* ── GUIDED TOUR ── */}
      <section id="product-tour" className="av-tour">
        <div className="av-tour-top">
          <div className="av-tour-top-left">
            <p className="av-eyebrow-sm">INSIDE THE PLATFORM</p>
            <h2 className="av-tour-title">A guided tour</h2>
          </div>
          <p className="av-tour-hint">
            Click any capability to see what aivionScore does — and how it shows up in your day-to-day.
          </p>
        </div>

        <div className="av-tour-body">
          {/* left list */}
          <ul className="av-cap-list">
            {capabilities.map((cap, i) => (
              <li
                key={cap.id}
                className={`av-cap-item${active === i ? " av-cap-active" : ""}`}
                onClick={() => setActive(i)}
              >
                <span className={`av-cap-num${active === i ? " av-cap-num-active" : ""}`}>{cap.id}</span>
                <div className="av-cap-body">
                  <strong>{cap.title}</strong>
                  <span>{cap.short}</span>
                </div>
                {active === i && <span className="av-cap-arr">&#8594;</span>}
              </li>
            ))}
          </ul>

          {/* right detail — two cards stacked */}
          <div className="av-detail-wrap">
            <div className="av-detail-top-card">
              <p className="av-detail-label">CAPABILITY {capabilities[active].id}</p>
              <h3 className="av-detail-title">{capabilities[active].title}</h3>
            </div>
            <div className="av-detail-bottom-card">
              <p>{capabilities[active].detail}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEPLOY ── */}
      <section className="av-deploy">
        <div className="av-deploy-inner">
          <p className="av-eyebrow av-center">FROM SIGN-OFF TO LIVE</p>
          <h2 className="av-deploy-title">How aivionScore gets deployed</h2>
          <div className="av-deploy-grid">
            {deploySteps.map((s) => (
              <div key={s.id} className="av-deploy-col">
                <div className="av-deploy-circle">{s.id}</div>
                <div className="av-deploy-card">
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUTCOMES ── */}
      <section className="av-outcomes">
        <div className="av-outcomes-inner">
          <div className="av-outcomes-left">
            <p className="av-eyebrow-sm">WHY TEAMS CHOOSE IT</p>
            <h2 className="av-outcomes-title">Outcomes, not<br />output.</h2>
            <p className="av-outcomes-desc">
              aivionScore is purpose-built to move the metrics your
              team is measured on — not just to add another dashboard.
            </p>
          </div>
          <div className="av-outcomes-grid">
            {outcomes.map((o, i) => (
              <div key={i} className="av-outcome-card">
                <span className="av-outcome-check">&#10003;</span>
                <p>{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MORE PRODUCTS ── */}
      <section className="av-more">
        <div className="av-more-inner">
          <div className="av-more-top">
            <div>
              <p className="av-eyebrow-sm">THE PRODUCT FAMILY</p>
              <h2 className="av-more-title">More from AIVION</h2>
            </div>
            <a className="av-more-all" href="/#products">All products &#8594;</a>
          </div>
          <div className="av-more-grid">
            {products.map((p) => (
              p.link.startsWith("http") ? (
                <a key={p.name} href={p.link} target="_blank" rel="noreferrer" className="av-prod-card">
                  <div className="av-prod-top">
                    <span className="av-prod-tag">PRODUCT</span>
                    <span className="av-prod-arrow">↗</span>
                  </div>
                  <h4>{p.name}</h4>
                  <p>{p.tag}</p>
                </a>
              ) : (
                <Link key={p.name} to={p.link} className="av-prod-card">
                  <div className="av-prod-top">
                    <span className="av-prod-tag">PRODUCT</span>
                    <span className="av-prod-arrow">↗</span>
                  </div>
                  <h4>{p.name}</h4>
                  <p>{p.tag}</p>
                </Link>
              )
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="av-cta">
        <div className="av-cta-inner">
          <div className="av-cta-left">
            <p className="av-eyebrow-xs">SEE IT ON YOUR DATA</p>
            <h2 className="av-cta-title">Get a working demo of aivionScore.</h2>
            <p className="av-cta-desc">
              We'll run a tailored walkthrough on a sample of your own data — so you can judge the value,
              not just the slides.
            </p>
          </div>
          <div className="av-cta-right">
            <a href="/#contact" className="av-cta-btn-outline" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>BOOK A DEMO &#8594;</a>
            <button className="av-cta-btn-ghost" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>SEE IT IN ACTION</button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="av-footer">
        <div className="av-footer-inner">
          <div className="av-footer-brand">
            <div className="av-footer-logo">
              <span className="av-logo-aiv">AIV</span><span className="av-logo-ion">ION</span>
            </div>
            <p>Delivering meaningful outcomes through intelligent AI solutions.</p>
            <div className="av-footer-icons">
              <span className="av-icon">in</span>
              <span className="av-icon">&#9993;</span>
            </div>
          </div>
          <div className="av-footer-col">
            <p className="av-footer-heading">QUICK LINKS</p>
            <div className="footer-quick-links-grid">
              {footerLinks.map((l) => <a key={l.label} href={l.href} className="av-footer-link" onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a>)}
            </div>
          </div>
        </div>
        <div className="av-footer-bottom">
          <p>&#169; 2026 AIVION. All rights reserved.</p>
          
        </div>
      </footer>

    </div>
  );
}
